/**
 * M3X.WORKSPACE-CLOSURE Session 2 PR 3 — RegistryDrivenPage with
 * a real DataTable shell that shows column headers + a quiet
 * inline empty row.
 *
 * Before: rendered a giant Database icon + multi-line description
 * via <EmptyState />. Read as "no DataTable here".
 *
 * After: the DataTable renders its real chrome (column headers,
 * border, hover affordances) and a single muted-italic centered
 * empty row inside tbody. Visually communicates "this WILL be
 * a populated list once the aggregator lands" without faking data.
 *
 * The placeholder column set (Code · Name · Status · Updated) is
 * used when the registry has no per-screen column override; this
 * matches the spec's "4-column shell".
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "../components/ui/DataTable";
import { screenForRoute } from "../navigation/registry";
import { AiBriefPanel } from "./AiBriefPanel";


// Empty placeholder row shape. Replaced screen-by-screen as
// per-screen aggregators land in Session 3+.
interface PlaceholderRow {
  code: string;
  name: string;
  status: string;
  updated: string;
}


export function RegistryDrivenPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isAr = i18n.language === "ar";
  const match = screenForRoute(location.pathname);

  // 4-column placeholder per closure-prompt §3 stricter bar:
  // Code · Name · Status · Updated. Header style is uppercase,
  // tracking-wide, 10px — DataTable's own thead styling applies.
  const columns = useMemo<ColumnDef<PlaceholderRow, unknown>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: t("workspace.column_code", "Code"),
        size: 120,
      },
      {
        id: "name",
        accessorKey: "name",
        header: t("workspace.column_name", "Name"),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("workspace.column_status", "Status"),
        size: 100,
      },
      {
        id: "updated",
        accessorKey: "updated",
        header: t("workspace.column_updated", "Updated"),
        size: 140,
      },
    ],
    [t],
  );

  if (!match) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        {t(
          "workspace.not_in_registry",
          "This route isn't in the navigation registry. Add it to console/src/navigation/registry.ts.",
        )}
      </div>
    );
  }

  const { subDomain, screen } = match;
  const title = isAr ? screen.labelAr : screen.labelEn;
  const subDomainLabel = isAr ? subDomain.labelAr : subDomain.labelEn;
  const Icon = subDomain.icon;
  const agent = screen.briefAgent ?? subDomain.defaultBriefAgent ?? "tashghil";

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_280px]">
      <div className="space-y-4">
        <header className="flex items-start gap-3">
          <Icon className="mt-1 h-6 w-6 text-text-tertiary" />
          <div>
            <p className="text-xs uppercase tracking-wide text-text-tertiary">
              {subDomainLabel}
            </p>
            <h1 className="text-2xl font-bold text-text-primary">
              {title}
            </h1>
          </div>
        </header>

        {/* Real DataTable shell — keepShellOnEmpty renders the column
            headers + a single muted-italic empty row inside tbody.
            No Database icon, no replacement panel. */}
        <DataTable<PlaceholderRow>
          columns={columns}
          data={[]}
          loading={false}
          keepShellOnEmpty
          exportFilename={`workspace-${screen.id}`}
          hideBulkSelection
        />
      </div>

      <aside className="hidden xl:block">
        <div className="sticky top-0 h-full rounded-lg border border-border-default bg-surface-default">
          <AiBriefPanel agent={agent} locale={isAr ? "ar" : "en"} />
        </div>
      </aside>
    </div>
  );
}


export default RegistryDrivenPage;
